import { useMemo } from "react";
import "./MindMappingReportBNinthSection.css";

/* ── Scoring helpers ─────────────────────────────────────────── */

function scoreAge(age) {
  const n = parseInt(age, 10);
  if (isNaN(n))  return { score: 60, label: "Moderate", note: "Age not specified — general readiness assumed." };
  if (n < 26)    return { score: 85, label: "Strong",   note: "Prime experimentation window — low personal obligations enable bold moves." };
  if (n <= 32)   return { score: 92, label: "Excellent",note: "Peak founder window — energy, ambition, and emerging expertise converge." };
  if (n <= 42)   return { score: 80, label: "Strong",   note: "Domain-depth advantage — experience accelerates credibility and B2B traction." };
  return         { score: 72, label: "Good",    note: "Seasoned operator profile — network and capital access compensate for higher risk aversion." };
}

function scoreCourse(course) {
  if (!course) return { score: 60, label: "Moderate", note: "Course not specified — general knowledge base assumed." };
  const c = course.toLowerCase();
  if (/b\.?tech|b\.?e|engineering/i.test(c))    return { score: 95, label: "Excellent", note: "Engineering background unlocks technical product building and deep-tech investor credibility." };
  if (/mba|bba/i.test(c))                        return { score: 90, label: "Excellent", note: "Business education provides market sizing, fundraising, and GTM strategy fluency." };
  if (/b\.?sc|msc|science/i.test(c))             return { score: 85, label: "Strong",   note: "Science background supports research-intensive, data-driven, or IP-based startups." };
  if (/bca|mca|computer/i.test(c))               return { score: 88, label: "Strong",   note: "CS/IT background enables rapid prototyping — strong signal for product-led growth." };
  if (/b\.?com|finance|account/i.test(c))        return { score: 80, label: "Strong",   note: "Finance background gives unit economics depth — valued in fintech or marketplace models." };
  if (/design|b\.?des|bfa/i.test(c))             return { score: 78, label: "Good",     note: "Design thinking creates UX differentiation — strong for consumer and D2C startups." };
  return { score: 65, label: "Good", note: "Interdisciplinary backgrounds often bring unique market perspectives." };
}

function scoreSector(type) {
  const t = (type ?? "tertiary").toLowerCase();
  if (t === "tertiary")  return { score: 88, label: "Strong",   note: "Services/tech sectors have the lowest barrier to entry and highest investor activity in India." };
  if (t === "secondary") return { score: 72, label: "Good",     note: "Manufacturing/industrial sectors reward patient capital — PLI schemes add tailwinds." };
  if (t === "primary")   return { score: 65, label: "Moderate", note: "Primary sector startups solve large social problems but require long distribution build-out." };
  return { score: 70, label: "Good", note: "Sector profile available." };
}

function scoreFunds(affordability) {
  if (!affordability) return { score: 60, label: "Moderate", note: "Fund level not specified — bootstrap assumption applied." };
  const a = affordability.toString().toLowerCase();
  if (a.includes("100"))  return { score: 95, label: "Excellent", note: "Institutional or HNI-backed — very strong runway, high scale potential." };
  if (a.includes("10-20"))return { score: 85, label: "Strong",   note: "Serious scale capital — enables aggressive hiring and GTM execution." };
  if (a.includes("5-7"))  return { score: 78, label: "Good",     note: "Good early-stage commitment — sufficient for MVP to Series A bridge." };
  if (a.includes("1-2"))  return { score: 68, label: "Moderate", note: "Early self-funded — maintain lean burn and aim for revenue quickly." };
  return { score: 58, label: "Early Stage", note: "Bootstrap start — focus on revenue-first model to extend runway without external dilution." };
}

function scoreLocation(state) {
  const TIER1 = ["maharashtra","karnataka","delhi","tamil nadu","telangana","haryana","gujarat","andhra pradesh"];
  const TIER2 = ["rajasthan","kerala","west bengal","madhya pradesh","uttar pradesh","punjab","odisha"];
  if (!state) return { score: 65, label: "Moderate", note: "Location not specified." };
  const s = state.toLowerCase();
  if (TIER1.some(t => s.includes(t))) return { score: 88, label: "Strong",   note: "Tier-1 ecosystem — high investor visibility, startup events, and talent density." };
  if (TIER2.some(t => s.includes(t))) return { score: 74, label: "Good",     note: "Tier-2 ecosystem — growing infrastructure; consider remote-first with occasional Tier-1 presence." };
  return { score: 70, label: "Good", note: "Tier-3 market — first-mover advantage and strong government grant access offset lower ecosystem density." };
}

function scorePersonality(intro, think, judge, sense) {
  let score = 70;
  const notes = [];
  if (intro === "Extravert")   { score += 8;  notes.push("Extrovert edge for fundraising and sales."); }
  if (think === "Thinking")    { score += 6;  notes.push("Analytical decision-making — strong for scaling."); }
  if (judge === "Judging")     { score += 7;  notes.push("Execution discipline — investors value milestone adherence."); }
  if (sense === "Intuition")   { score += 5;  notes.push("Visionary profile — strong for VC-scale bets."); }
  const clamped = Math.min(score, 97);
  return {
    score:  clamped,
    label:  clamped >= 85 ? "Excellent" : clamped >= 75 ? "Strong" : "Good",
    note:   notes.length > 0 ? notes.join(" ") : "Balanced personality profile — well-rounded across startup dimensions.",
  };
}

function scoreGoals(goals) {
  if (!goals || goals.length === 0) return { score: 60, label: "Moderate", note: "No goals selected — strategic focus is unclear." };
  const n = goals.length;
  if (n >= 6) return { score: 88, label: "Strong",   note: `${n} goals selected — comprehensive life-goal framework provides multi-vector startup opportunity identification.` };
  if (n >= 4) return { score: 78, label: "Good",     note: `${n} goals selected — focused strategic intent with clear priority areas.` };
  return       { score: 68, label: "Moderate", note: `${n} goal(s) selected — consider expanding to capture broader opportunity mapping.` };
}

const SCORE_COLOR = (s) => s >= 85 ? "#059669" : s >= 75 ? "#0891b2" : s >= 65 ? "#f59e0b" : "#e74c3c";
const LABEL_COLOR = { Excellent: "#059669", Strong: "#0891b2", Good: "#f59e0b", Moderate: "#e74c3c", "Early Stage": "#e74c3c" };

const MindMappingReportBNinthSection = ({ res, toggle }) => {
  const dims = useMemo(() => {
    const age  = scoreAge(res?.age);
    const crs  = scoreCourse(res?.course);
    const sec  = scoreSector(res?.sector_type);
    const fnd  = scoreFunds(res?.affordability);
    const loc  = scoreLocation(res?.state);
    const per  = scorePersonality(res?.trait_intro_extro, res?.trait_think_feel, res?.trait_judge_perceive, res?.trait_sense_intuit);
    const gls  = scoreGoals(res?.goals);

    return [
      { label: "Age & Founder Window",      ...age,  sno: "9 A" },
      { label: "Course Accessibility",       ...crs,  sno: "" },
      { label: "Sector Accessibility",       ...sec,  sno: "" },
      { label: "Fund Level Readiness",       ...fnd,  sno: "" },
      { label: "Location Advantage",         ...loc,  sno: "" },
      { label: "Personality-Founder Fit",    ...per,  sno: "" },
      { label: "Goal Alignment Breadth",     ...gls,  sno: "" },
    ];
  }, [res]);

  const overallScore = useMemo(() => {
    const avg = Math.round(dims.reduce((s, d) => s + d.score, 0) / dims.length);
    return avg;
  }, [dims]);

  const overallLabel =
    overallScore >= 85 ? "Launch-Ready — Strong Founder Fit" :
    overallScore >= 75 ? "Pre-Validation — Solid Foundation" :
    overallScore >= 65 ? "Building Phase — Key Gaps to Address" :
                         "Early Stage — Focus on Fundamentals";

  const topStrengths = useMemo(() =>
    [...dims].sort((a, b) => b.score - a.score).slice(0, 3), [dims]);

  const topGaps = useMemo(() =>
    [...dims].sort((a, b) => a.score - b.score).slice(0, 2), [dims]);

  const nextStep =
    overallScore >= 85
      ? "Begin customer discovery interviews (30+) and set your North Star Metric. You are ready to engage an incubator or angel investor."
      : overallScore >= 75
      ? "Validate your top 2-3 startup ideas with 15 customer conversations. Apply to a DPIIT-recognized incubation program in the next 60 days."
      : overallScore >= 65
      ? "Strengthen your weakest dimension (see gaps below) before committing to a sector. Consider a co-founder with complementary skills."
      : "Focus on building domain expertise and financial runway before launching. Explore government grants and MSME support programs first.";

  return (
    <div className="mm-rb-ninth-container">
      <div className={toggle ? "table-adjust" : "mm-rb-ninth-final-table"}>
        <span className="mm-rb-ninth-corner mm-rb-ninth-corner-top-left" />
        <span className="mm-rb-ninth-corner mm-rb-ninth-corner-top-right" />
        <span className="mm-rb-ninth-corner mm-rb-ninth-corner-bottom-left" />
        <span className="mm-rb-ninth-corner mm-rb-ninth-corner-bottom-right" />

        <div className="mm-rb-ninth-module-title">
          <span className="module-prefix">Module 9:</span> Founder Fit Score &amp; Strategic Readiness Summary
          <span className="mm-rb-premium-tag">★ Report A+ Exclusive</span>
        </div>

        {/* ── Score overview banner ─────────────────────────────── */}
        <div className="mm-rb-ninth-overview">
          <div className="mm-rb-ninth-score-circle" style={{ borderColor: SCORE_COLOR(overallScore) }}>
            <span className="mm-rb-ninth-score-num" style={{ color: SCORE_COLOR(overallScore) }}>
              {overallScore}
            </span>
            <span className="mm-rb-ninth-score-denom">/100</span>
          </div>
          <div className="mm-rb-ninth-overview-right">
            <div className="mm-rb-ninth-overall-label" style={{ color: SCORE_COLOR(overallScore) }}>
              {overallLabel}
            </div>
            <div className="mm-rb-ninth-next-step">
              <strong>Recommended Next Step:</strong> {nextStep}
            </div>
          </div>
        </div>

        {/* ── Dimension table ───────────────────────────────────── */}
        <div className="mm-rb-ninth-table-scroll-wrapper">
          <table className="mm-rb-ninth-inner-table">
            <thead>
              <tr>
                <th className="mm-rb-ninth-col-sno">S.NO</th>
                <th>Dimension</th>
                <th>Score</th>
                <th>Readiness Level</th>
                <th>Key Insight</th>
              </tr>
            </thead>
            <tbody>
              {dims.map((d, i) => (
                <tr key={i}>
                  {i === 0 && (
                    <td className="mm-rb-ninth-col-sno" rowSpan={dims.length}>9 A</td>
                  )}
                  <td><strong>{d.label}</strong></td>
                  <td>
                    <div className="mm-rb-ninth-bar-wrap">
                      <div
                        className="mm-rb-ninth-bar"
                        style={{ width: `${d.score}%`, backgroundColor: SCORE_COLOR(d.score) }}
                      />
                      <span className="mm-rb-ninth-bar-num" style={{ color: SCORE_COLOR(d.score) }}>
                        {d.score}%
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className="mm-rb-ninth-level-badge"
                      style={{ backgroundColor: LABEL_COLOR[d.label] ?? "#888" }}>
                      {d.label}
                    </span>
                  </td>
                  <td className="mm-rb-ninth-highlight">{d.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Strengths & Gaps ─────────────────────────────────── */}
        <div className="mm-rb-ninth-sg-grid">
          <div className="mm-rb-ninth-sg-card strengths">
            <div className="mm-rb-ninth-sg-title">✅ Top Strengths</div>
            <ul className="mm-rb-ninth-sg-list">
              {topStrengths.map((d, i) => (
                <li key={i}><strong>{d.label}</strong> — {d.score}% · {d.note}</li>
              ))}
            </ul>
          </div>
          <div className="mm-rb-ninth-sg-card gaps">
            <div className="mm-rb-ninth-sg-title">⚠️ Key Gaps to Address</div>
            <ul className="mm-rb-ninth-sg-list">
              {topGaps.map((d, i) => (
                <li key={i}><strong>{d.label}</strong> — {d.score}% · {d.note}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="mm-rb-ninth-footer">
          © MarsMate AI · Founder Fit Score is a composite assessment based on 7 weighted dimensions.
          Use this as a directional guide, not an absolute ranking.
        </div>
      </div>
    </div>
  );
};

export default MindMappingReportBNinthSection;
